import { type MutableRefObject, useContext, useMemo, useRef } from 'react';
import { type NavigateFunction, useNavigate } from '@leagueplatform/routing';
import { useIntl, type UseIntlReturn } from '@leagueplatform/locales';
import { captureError } from '@leagueplatform/observability';

import { checkLoop } from './utils/loop-detector';
import { MasonryEngineValidationController } from './masonry-engine-validation-controller';
import { formatEventName } from './utils/formatEventName';
import {
  DEFAULT_NAMESPACE,
  LOOP_PER_NODE_MAX_COUNT,
} from './constants/constants';
import type {
  MasonryEngineNodeAction,
  AnyMasonryEngineNodeAction,
  MasonryEngineNodeAncestry,
  MasonryEngineNode,
} from './types/masonry-engine-node.types';
import {
  type MasonryEngineActionController,
  type MasonryEngineActionHandlerMap,
  MasonryEngineEvent,
  type MasonryEngineActionHandler,
  type MasonryEngineEventListener,
  type InvokeActionFn,
  type InvokedAction,
  type InvokeActionFnResourcesParam,
  type MasonryActionResult,
  type MasonryActionResultFailure,
  MasonryActionException,
} from './types/masonry-engine-action.types';
import { MasonryEngineNodeAncestryContext } from './masonry-engine-node-ancestry-context';
import { useMasonryEngineConfig } from './masonry-engine-config-context';
import { type InstanceResources } from './types/masonry-engine-config.types';
import { withNormalization } from './utils/normalize-action-handler-return';
import { resolveNextAction } from './utils/resolve-next-action';
import { fallbackActionHandler } from './utils/fallback-action-handler';
import { sendActionStartedEventToDevtools } from './utils/send-action-started-event-to-devtools';
import { collectChainedActionResults } from './utils/collect-chained-action-results';

/* eslint-disable no-underscore-dangle */

type AnyMasonryNodeActionHandler =
  MasonryEngineActionHandler<AnyMasonryEngineNodeAction>;

type AnyMasonryEngineActionHandlerMap = Partial<
  Record<string, AnyMasonryNodeActionHandler>
>;

type EventListenerKey = `${string}-${MasonryEngineEvent}-${string}`;

export type EventListenerMap = Partial<
  Record<
    EventListenerKey,
    Set<MasonryEngineEventListener<AnyMasonryEngineNodeAction>>
  >
>;

const masonryEngineEventNamesExceptALL = Object.values(
  MasonryEngineEvent,
).filter((type) => type !== MasonryEngineEvent.ALL);

export const createMasonryEngineActionController = <
  /**
   * A union of known `MasonryEngineNodeAction` supported for this controller.
   */
  KnownActions extends MasonryEngineNodeAction,
>(
  defaultHandlers: MasonryEngineActionHandlerMap<KnownActions>,
): MasonryEngineActionController<KnownActions> => {
  const actionRegistry: Partial<
    Record<string, AnyMasonryEngineActionHandlerMap>
  > = {
    default: defaultHandlers as unknown as AnyMasonryEngineActionHandlerMap,
  };

  const universalHandlerRegistry: Record<
    string,
    {
      handler: AnyMasonryNodeActionHandler;
      shouldSupersede: boolean;
    }
  > = {};

  const eventListenerMap: EventListenerMap = {};

  /**
   * @method registerHandler is used to register an action to the to the Masonry Engine Driver {@link actionRegistry }.
   */
  const registerHandler: MasonryEngineActionController<KnownActions>['registerHandler'] =
    (type, namespace, handler) => {
      actionRegistry[namespace] = actionRegistry[namespace] || {};

      if (actionRegistry[namespace]![type]) {
        throw new Error(
          'Attempting to register a handler for a type at a namespace where a handler was already registered for that type!',
        );
      }

      actionRegistry[namespace]![type] =
        handler as unknown as AnyMasonryNodeActionHandler;
    };

  const registerUniversalHandler: MasonryEngineActionController<KnownActions>['registerUniversalHandler'] =
    (type, handler, shouldSupersede = true) => {
      universalHandlerRegistry[type] = {
        handler: handler as unknown as AnyMasonryNodeActionHandler,
        shouldSupersede,
      };
    };

  /**
   * @method overrideHandler: is used to override an action in the Masonry Engine Driver {@link actionRegistry }.
   */
  const overrideHandler: MasonryEngineActionController<KnownActions>['overrideHandler'] =
    (type, namespace, handler) => {
      actionRegistry[namespace] = actionRegistry[namespace] || {};

      actionRegistry[namespace]![type] =
        handler as unknown as AnyMasonryNodeActionHandler;
    };

  // Central execution path for all actions. Handles DevTools wiring, parent/child
  // action correlation, and ensures nested `chainAction` calls reuse the same
  // resource tuple while threading the current actionEventId as parent metadata.
  const invokeActionInternal = (
    invokedAction: InvokedAction<AnyMasonryEngineNodeAction>,
    parentActionEventId: string | undefined,
    resources: InvokeActionFnResourcesParam,
  ) => {
    const action = {
      ...invokedAction,
      ancestry: invokedAction.ancestry || [],
    };

    // Guard against infinite action loops only for chains that originate from
    // a node's onLoad slot. State lives at module scope so it survives React
    // remounts and still catches cross-screen cycles.
    if (
      action.origin === 'onLoad' &&
      checkLoop(action, LOOP_PER_NODE_MAX_COUNT)
    ) {
      const nodeId = action.ancestry[0];
      const rootNodeId = action.ancestry.at(-1) as string;

      captureError(new Error('Masonry action loop detected'), {
        errorName: 'Masonry action loop detected',
        tags: {
          rootNodeId,
          nodeId,
          actionType: action.type,
          namespace: action.namespace ?? DEFAULT_NAMESPACE,
        },
      });
      resources.messagingController.sendMessageToInstance({
        type: 'showModal',
        payload: {
          title: 'Something went wrong',
          hideTitle: false,
          showCloseButton: true,
          content: {
            kind: 'text',
            body: 'This page encountered an error. Please try again or contact support.',
          },
          elementToFocusOnClose:
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : document.body,
        },
      });
      return {
        isSuccess: false,
        error: new Error('Masonry action loop detected'),
      } satisfies MasonryActionResultFailure;
    }

    const devTools = sendActionStartedEventToDevtools(action, {
      parentActionEventId,
    });
    const { actionEventId } = devTools;

    /**
     * an `invokeAction` function that doesn't
     * require passing resources, now that we have them from above.
     * Easier to call.
     */
    const invokeInnerAction: InvokeActionFn<
      AnyMasonryEngineNodeAction,
      true
    > = (innerAction) =>
      invokeActionInternal(
        {
          ...innerAction,
          origin: action.origin,
        } as InvokedAction<AnyMasonryEngineNodeAction>,
        actionEventId,
        resources,
      );

    /**
     * The handler is EITHER:
     * 1. the universal handler for this type if one exists and has `shouldSupersede` set to true;
     * 2. OTHERWISE, the action handler registered for this namespace, if one exists;
     * 3. OTHERWISE, the universal handler for this type, if one exists;
     * 4. OTHERWISE, the fallback handler;
     */
    const handler =
      (universalHandlerRegistry[action.type]?.shouldSupersede &&
        universalHandlerRegistry[action.type].handler) ||
      actionRegistry[action.namespace]?.[action.type] ||
      universalHandlerRegistry[action.type]?.handler ||
      fallbackActionHandler;

    const startedEventName = formatEventName(
      action.namespace,
      action.type,
      MasonryEngineEvent.STARTED,
    );

    /**
     * Trigger all Masonry event listeners registered for
     * {@link MASONRY_ENGINE_EVENT_TYPE MASONRY_ENGINE_EVENT_TYPE.EVENT_STARTED} event type
     */
    eventListenerMap[startedEventName]?.forEach((listener) => {
      listener(action, undefined);
    });

    // eslint-disable-next-line no-console
    console.debug('Masonry Engine Action emitted', action);

    /**
     * We return the full chain's Promise so the caller can await the entire
     * action chain, including any declarative onSuccess/onFailure/onException
     * chained actions. This allows callers (e.g. renderers) to track
     * loading/disabled state until the full chain completes.
     */
    return withNormalization(() =>
      handler(action, {
        ...resources,
        invokeAction: invokeInnerAction,
      }),
    )
      .then(async (result) => {
        // The handler did not throw or reject. We can now optionally invoke analytics
        // and any chained action.

        if (handler === fallbackActionHandler) {
          // This is the fallback handler, which means no handler was found for the action.
          // DO NOT emit "ended" events or invoke an analytics action.

          devTools.sendFailed(new Error('no registered handler'));
        } else {
          // This is a valid handler, so we can emit an "ended" event and
          // invoke an analytics action.
          devTools.sendEnded(result);

          const endedEventName = formatEventName(
            action.namespace,
            action.type,
            MasonryEngineEvent.ENDED,
          );

          /**
           * Trigger all Masonry event listeners registered for
           * {@link MASONRY_ENGINE_EVENT_TYPE MASONRY_ENGINE_EVENT_TYPE.EVENT_ENDED} event type
           */
          eventListenerMap[endedEventName]?.forEach((listener) => {
            listener(action, result);
          });

          // Get the analytics handler for the default namespace
          const analyticsHandler = actionRegistry[DEFAULT_NAMESPACE]?.analytics;

          // Trigger Masonry analytics event if action has an analytics object
          if (action.analytics && analyticsHandler) {
            const analyticsPayload = {
              type: 'analytics',
              namespace: DEFAULT_NAMESPACE,
              payload: action.analytics,
              ancestry: action.ancestry,
            };

            invokeInnerAction(analyticsPayload);
          }
        }

        const nextAction = resolveNextAction({
          action,
          invocationOutcome: 'result',
          result,
        });

        if (nextAction) {
          // Await the chained action so the full chain's result propagates
          // back to the original caller.
          let chainedActionResults: Array<
            MasonryActionResult | MasonryActionException
          > = [];

          try {
            const chainResult = await invokeInnerAction(nextAction);
            if (chainResult) {
              chainedActionResults = collectChainedActionResults(chainResult);
            }
          } catch (error) {
            chainedActionResults = collectChainedActionResults(
              new MasonryActionException(error),
            );
          }

          return {
            ...result,
            chainedActionResults,
          };
        }

        return result;
      })
      .catch(async (error) => {
        const nextAction = resolveNextAction({
          action,
          invocationOutcome: 'exception',
          exception: error,
        });

        if (nextAction) {
          try {
            await invokeInnerAction(nextAction);
          } catch {
            // Preserve the original rejection from the invoking action.
          }
        }

        // Capture the error and send a failed event to devtools
        captureError(error);
        devTools.sendFailed(error);

        throw error;
      });
  };

  const invokeAction: InvokeActionFn<AnyMasonryEngineNodeAction> = (
    invokedAction,
    resources,
  ) =>
    invokeActionInternal(
      invokedAction as InvokedAction<AnyMasonryEngineNodeAction>,
      undefined,
      resources,
    );

  /**
   * @method addEventListener is used for adding a listener for Masonry actions and events to the {@link EventListenerMap}.
   * */
  const addEventListener: MasonryEngineActionController<KnownActions>['addEventListener'] =
    (actionType, namespace, eventType, listener) => {
      // An array of event types we should bind this listener to.
      const resolvedEventTypes =
        eventType === MasonryEngineEvent.ALL
          ? masonryEngineEventNamesExceptALL
          : [eventType];

      // Add listener for each resolved event type
      resolvedEventTypes.forEach((resolvedEventType) => {
        const formattedEventName = formatEventName(
          namespace,
          actionType,
          resolvedEventType,
        );
        eventListenerMap[formattedEventName] =
          eventListenerMap[formattedEventName] || new Set();
        eventListenerMap[formattedEventName]!.add(
          listener as unknown as MasonryEngineEventListener<AnyMasonryEngineNodeAction>,
        );
      });

      return () => {
        resolvedEventTypes.forEach((resolvedEventType) => {
          const formattedEventName = formatEventName(
            namespace,
            actionType,
            resolvedEventType,
          );

          eventListenerMap[formattedEventName]!.delete(
            listener as unknown as MasonryEngineEventListener<AnyMasonryEngineNodeAction>,
          );
        });
      };
    };

  /**
   * @method getRegisteredEventListeners: is used for getting an object that lists all the event types that have registered listeners in the Masonry Engine Listener Map {@link EventListenerMap }.
   */
  const getRegisteredEventListeners = () => {
    const record: Record<string, number> = {};

    Object.entries(eventListenerMap).forEach(([eventType, listenerSet]) => {
      if (listenerSet?.size) {
        record[eventType] = listenerSet.size;
      }
    });

    /**
     * This is an object with keys representing event types and values representing
     * how many listeners are currently registered for each.
     */
    return record;
  };

  return {
    registerHandler,
    registerUniversalHandler,
    overrideHandler,
    invokeAction,
    addEventListener,
    getRegisteredEventListeners,
  };
};

type CreateInvokeActionFnOptions<Action extends MasonryEngineNodeAction> = {
  actionController: MasonryEngineActionController<Action>;
  instanceResources: InstanceResources;
  navigateFnRef: MutableRefObject<NavigateFunction>;
  intlRef: MutableRefObject<UseIntlReturn>;
  ancestry: MasonryEngineNodeAncestry;
};

/**
 * A factory for creating an `invokeAction` fuction which holds all instance resources in closure
 *
 * Used internally by `useMasonryEngineActionEmitter` below, which grabs those resources from Context
 * (being called in a Node renderer) and passes them along to this function.
 */
export const createInvokeActionFn =
  <Action extends MasonryEngineNodeAction>({
    actionController,
    instanceResources,
    navigateFnRef,
    intlRef,
    ancestry,
  }: CreateInvokeActionFnOptions<Action>): InvokeActionFn<Action, true> =>
  (invokedAction) => {
    // Persist React 16 SyntheticEvents so event data remains available
    // across async boundaries in chained actions.
    invokedAction.event?.persist?.();

    // Inject ancestry if the passed object does not already include it.
    const action: typeof invokedAction = {
      ...invokedAction,
      currentTarget:
        invokedAction.currentTarget ?? invokedAction.event?.currentTarget,
      ancestry: invokedAction.ancestry ?? ancestry,
    };

    return actionController.invokeAction(action, {
      stateController: instanceResources.stateControllerStore.getState(),
      messagingController: instanceResources.messagingController,
      validationController: MasonryEngineValidationController,
      navigate: navigateFnRef.current,
      intl: intlRef.current,
    });
  };

/**
 * A hook that returns an {@link InvokeActionFn `InvokeActionFn`}. Automatically includes
 * all of the instance resources required by action handlers.
 */
export const useMasonryEngineActionEmitter = <
  KnownActions extends MasonryEngineNodeAction,
>(): InvokeActionFn<KnownActions, true> => {
  const ancestry = useContext(MasonryEngineNodeAncestryContext);

  const { actionController, stateControllerStore, messagingController } =
    useMasonryEngineConfig<MasonryEngineNode, KnownActions>();

  const navigate = useNavigate();
  const navigateFnRef = useRef(navigate);
  navigateFnRef.current = navigate;

  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;

  return useMemo(
    () =>
      createInvokeActionFn<KnownActions>({
        actionController,
        instanceResources: {
          stateControllerStore,
          messagingController,
        },
        navigateFnRef,
        intlRef,
        ancestry,
      }),
    [actionController, ancestry, messagingController, stateControllerStore],
  );
};
